import React, { useEffect, useState } from 'react';
import {
  X,
  Sparkles,
  Database,
  ExternalLink,
  Globe,
  Loader
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { workspaceApi } from '../../api/workspaceApi';
import { useSampleTemplateThumbnailSrc } from '../../hooks/useSampleTemplateThumbnailSrc';
import { CatalogLicenseLine, CatalogSampleLicenseDisclaimer } from './CatalogLicenseNotice';
import { formatSourceLinkLabel } from '../../lib/catalogSampleDisplay';

function formatTableLabel(tableId) {
  if (!tableId) return 'Dataset';
  const parts = String(tableId).split('/');
  return parts[parts.length - 1] || tableId;
}

const SampleWorkspaceTemplateModal = ({
  templateSummary,
  isOpen,
  onClose,
  onUse,
  isCreating
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const filename = templateSummary?.filename;
  const { displaySrc: thumbSrc, loadFailed, setLoadFailedFromImg } = useSampleTemplateThumbnailSrc(
    isOpen ? (detail?.thumbnail ?? templateSummary?.thumbnail) : null
  );
  const showHeroImage = Boolean(thumbSrc) && !loadFailed;

  useEffect(() => {
    if (!isOpen || !filename) {
      setDetail(null);
      setLoadError(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    workspaceApi
      .getWorkspaceTemplateDetail(filename)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        console.error('Failed to load template detail:', err);
        if (!cancelled) {
          setLoadError(true);
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, filename]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !templateSummary) return null;

  const display = detail || templateSummary;
  const datasets = Array.isArray(display.datasets) ? display.datasets : [];
  const exampleQuestions = Array.isArray(display.example_questions)
    ? display.example_questions
    : [];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Sample workspace details for ${display.name}`}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
      <div
        className="relative flex max-h-[min(90vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-amber-200/60 bg-white shadow-2xl dark:border-amber-900/40 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-36 w-full shrink-0 bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-800 dark:to-slate-900">
          {showHeroImage ? (
            <img
              src={thumbSrc}
              alt=""
              className="h-full w-full object-cover saturate-[0.85] brightness-[0.92]"
              onError={() => setLoadFailedFromImg()}
            />
          ) : null}
          <div
            className={cn(
              'absolute inset-0',
              showHeroImage
                ? 'bg-gradient-to-t from-black/50 to-transparent'
                : 'bg-gradient-to-t from-black/20 to-transparent'
            )}
          />
          <div className="absolute left-4 top-4 rounded-lg bg-black/40 p-1.5 text-amber-200 backdrop-blur-sm">
            <Sparkles size={16} strokeWidth={2.25} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 rounded-lg bg-black/40 p-2 text-white/90 backdrop-blur-sm transition-colors hover:bg-black/55 hover:text-white"
            aria-label="Close sample workspace details"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">
          <CatalogSampleLicenseDisclaimer className="mb-5" />

          <div className="mb-4 min-w-0">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">
              Sample workspace
            </p>
            <h2 className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">
              {display.name}
            </h2>
          </div>

          {display.description ? (
            <p className="mb-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {display.description}
            </p>
          ) : null}

          {exampleQuestions.length > 0 ? (
            <section className="mb-5">
              <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
                Example questions
              </p>
              <ul className="space-y-2">
                {exampleQuestions.map((q) => (
                  <li
                    key={q}
                    className="rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 text-sm leading-snug text-slate-700 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300"
                  >
                    {q}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="mb-5">
            <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
              Datasets ({display.dataset_count ?? datasets.length})
            </p>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
                <Loader size={18} className="animate-spin" />
                <span className="text-xs font-semibold uppercase tracking-widest">
                  Loading dataset details…
                </span>
              </div>
            ) : loadError ? (
              <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                Could not load dataset license information. You can still create this workspace.
              </p>
            ) : datasets.length > 0 ? (
              <ul className="space-y-3">
                {datasets.map((ds) => (
                  <li
                    key={ds.table_id}
                    className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-800/40"
                  >
                    <div className="mb-1.5 flex items-start gap-2">
                      <Database size={14} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">
                          {formatTableLabel(ds.table_id)}
                        </p>
                        {ds.table_id !== formatTableLabel(ds.table_id) ? (
                          <p className="mt-0.5 truncate font-mono text-[10px] text-slate-400">
                            {ds.table_id}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {ds.summary ? (
                      <p className="mb-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                        {ds.summary}
                      </p>
                    ) : null}
                    <dl className="space-y-1.5 text-xs">
                      <div>
                        <dt className="font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-500">
                          Source
                        </dt>
                        <dd className="mt-0.5">
                          {ds.url ? (
                            <a
                              href={ds.url}
                              title={ds.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex max-w-full items-center gap-1 font-medium text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-400"
                            >
                              <Globe size={12} className="shrink-0" aria-hidden />
                              <span className="truncate">{formatSourceLinkLabel(ds.url)}</span>
                              <ExternalLink size={12} className="shrink-0 opacity-70" aria-hidden />
                            </a>
                          ) : (
                            <span className="text-slate-500 dark:text-slate-400">Not specified</span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-500">
                          License
                        </dt>
                        <dd className="mt-0.5">
                          {ds.license ? (
                            <CatalogLicenseLine license={ds.license} showLabel={false} />
                          ) : (
                            <span className="text-slate-500 dark:text-slate-400">Not specified</span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                No datasets in this template.
              </p>
            )}
          </section>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/80 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/80">
          <button
            type="button"
            onClick={onClose}
            disabled={isCreating}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-white disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onUse(filename)}
            disabled={isCreating}
            className="rounded-xl bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-70"
          >
            {isCreating ? 'Starting…' : 'Get started'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SampleWorkspaceTemplateModal;
